/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useState, useMemo } from 'react';

import debounce from 'lodash/debounce';
import {
  FieldProps,
  FormSpy,
  useField,
  useForm,
  FieldRenderProps,
} from 'react-final-form';
import { Collapse } from '@mui/material';

import { TextInput as Input } from '@vk-hr-tek/ui/input';

import { Autorecognition } from './Autorecognition';

/**
 * @typedef {Function} ValidateType
 * @param {*} value - Значение поля для валидации
 * @param {Object} [allValues] - Все значения формы
 * @returns {(string|undefined)|Promise<string|undefined>} - Ошибка валидации или undefined
 */

type ValidateType = (
  value: any,
  allValues?: any,
) => (string | undefined) | Promise<string | undefined>;

/**
 * Компонент TextInput
 * @param {Object} props - Свойства компонента
 * @param {string} [props.label] - Метка поля
 * @param {string} props.name - Имя поля
 * @param {Function} [props.onChange] - Колбэк на изменение значения
 * @param {string} [props.type] - Тип поля ввода
 * @param {string} [props.mask] - Маска ввода
 * @param {string} [props.placeholder] - Подсказка для поля ввода
 * @param {boolean} [props.multiline=false] - Флаг для многострочного поля
 * @param {number} [props.rows=1] - Количество строк для многострочного поля
 * @param {boolean} [props.showError=true] - Флаг для отображения ошибки
 * @param {boolean} [props.required=false] - Флаг для обязательного поля
 * @param {React.ReactNode} [props.tooltip] - Всплывающая подсказка
 * @param {Function} [props.after] - Функция для отображения дополнительного контента после поля
 * @param {boolean} [props.disabled=false] - Флаг для отключения поля
 * @param {boolean} [props.loading=false] - Флаг для отображения состояния загрузки
 * @param {boolean} [props.clearable=false] - Флаг для очистки поля
 * @param {string} [props.recognizedValue] - Распознанное значение
 * @param {boolean} [props.isRecognitionUsedBefore=false] - Флаг использования распознавания ранее
 * @param {boolean} [props.validationWithoutTouch=false] - Флаг валидации без взаимодействия с полем
 * @param {boolean} [props.validationWhenDisabled=false] - Флаг валидации когда поле отключено
 * @param {string} [props.inputMode] - Режим ввода
 * @param {string} [props.autocomplete] - Атрибут автозаполнения
 * @param {string} [props.testId] - Идентификатор для тестирования
 * @param {ValidateType[]} [warnings] - Массив валидаторов для предупреждений.
 * @param {ValidateType[]} [props.validateArray] - Массив валидаторов
 * @param {number} [maxLength] - Максимальная длина вводимого текста
 * @param {Object} [props.rest] - Дополнительные свойства поля
 * @param {boolean} [props.validationWithoutTouch=false] - Показывать ошибку без взаимодействия с полем.
 * @param {boolean} [props.validationWhenDisabled=false] - Показывать ошибку если поле задизейблено.
 * @returns {React.ReactNode} - Рендеринг поля ввода с автозаполнением
 */
const TextInput = ({
  label,
  name,
  onChange,
  type,
  mask,
  placeholder,
  multiline = false,
  rows = 1,
  maxRows,
  showError = true,
  required = false,
  tooltip,
  after,
  disabled = false,
  loading = false,
  clearable = false,
  recognizedValue,
  isRecognitionUsedBefore = false,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  inputMode,
  autocomplete,
  testId,
  validateArray,
  warnings,
  maxLength,
  helperText,
  debounceTimeout,
  ...rest
}: {
  after?: (value: string | undefined) => React.ReactNode;
  debounceTimeout?: number;
} & FieldProps<string | undefined, FieldRenderProps<string | undefined>> & {
    validateArray?: (ValidateType | undefined)[];
    warnings?: ValidateType[] | undefined;
  }) => {
  const { mutators } = useForm();

  /**
   * Композитный валидатор
   * @param {ValidateType[]} validators - Массив валидаторов
   * @returns {Function} - Валидатор, объединяющий результаты всех переданных валидаторов
   */
  const composeValidators =
    (validators: (ValidateType | undefined)[]) =>
    (value: Record<string, any> | any, allValues: Record<string, any>) => {
      const result = validators.reduce(
        (error: any, validator?: ValidateType) =>
          error || validator?.(value, allValues),
        undefined,
      );
      return result;
    };

  const composedWarnings = composeValidators(warnings || []);

  const { input, meta } = useField(name, {
    validate: composeValidators(validateArray || []),
    ...rest,
  });

  const { onBlur, onChange: onInputChange } = input;

  const [value, setValue] = useState<string | undefined>(input.value);

  /**
   * Обработчик изменения значения поля
   * @param {string|undefined} value - Новое значение поля
   */
  const handleChangeForm = useCallback(
    (formValue?: string) => {
      onInputChange(formValue);

      if (onChange) {
        onChange(formValue);
      }
    },
    [onInputChange, onChange],
  );

  const handleChangeDebounce = useMemo(
    () => debounce(handleChangeForm, debounceTimeout),
    [debounceTimeout, handleChangeForm],
  );

  const handleChange = useCallback(
    (changedValue?: string) => {
      setValue(changedValue);

      if (debounceTimeout) {
        handleChangeDebounce(changedValue);

        if (changedValue === '') {
          handleChangeForm(changedValue);
        }
      } else {
        handleChangeForm(changedValue);
      }
    },
    [debounceTimeout, handleChangeDebounce, handleChangeForm],
  );

  /**
   * Флаг для отображения ошибки в зависимости от состояния disabled
   * @type {boolean}
   */
  const errorDisabled = disabled || !validationWhenDisabled;

  /**
   * Флаг для отображения ошибки
   * @type {boolean}
   */
  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  /**
   * Флаг для отображения ворнинга
   * @type {boolean}
   */
  const renderWarning =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.data?.warning
      : meta.touched && !!meta.data?.warning;

  return (
    <div>
      <Autorecognition
        recognizedValue={recognizedValue}
        isRecognitionUsedBefore={isRecognitionUsedBefore}
        onBlur={onBlur}
        disabled={disabled}
        setValue={input.onChange}
        value={value || input.value}
        mask={mask}
      >
        <Input
          {...input}
          testId={testId}
          label={label}
          required={required}
          disabled={disabled}
          value={value || input.value}
          onChange={handleChange}
          type={type}
          error={renderError}
          helperText={(renderError && showError && meta.error) || helperText}
          warning={renderWarning && meta.data?.warning}
          placeholder={placeholder}
          tooltip={tooltip}
          multiline={multiline}
          rows={rows}
          maxRows={maxRows}
          mask={mask}
          loading={loading}
          clearable={clearable}
          inputMode={inputMode}
          autocomplete={autocomplete}
          maxLength={maxLength}
        />

        <FormSpy
          subscription={{ values: true }}
          onChange={({ values }) => {
            if (values) {
              mutators.setFieldData(input.name, {
                warning: composedWarnings(values[input.name], values),
              });
            }
          }}
        />

        {after && typeof after === 'function' && (
          <Collapse in={meta.visited}>{after(input.value)}</Collapse>
        )}
      </Autorecognition>
    </div>
  );
};

export default TextInput;
